import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Radio, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(username, password);
      navigate(user.role === "admin" ? "/admin" : "/portal", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07090E] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="relative w-16 h-16 mx-auto mb-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
            <Radio className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-100 flex items-center justify-center gap-2">
            Scharpz Live!
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 pulse-dot mt-2" />
          </h1>
          <p className="ops-caption mt-2">Internet Intercom & SIP Console</p>
        </div>

        <form onSubmit={onSubmit} className="ops-card p-6 space-y-5" data-testid="login-form">
          <div className="space-y-2">
            <Label htmlFor="username" className="text-slate-300">Username</Label>
            <Input id="username" data-testid="login-username-input" value={username}
              onChange={(e) => setUsername(e.target.value)} autoComplete="username" required
              className="bg-[#0A0D14] border-[#1E2638] text-slate-100 focus-visible:ring-emerald-500" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-slate-300">Password</Label>
            <Input id="password" type="password" data-testid="login-password-input" value={password}
              onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required
              className="bg-[#0A0D14] border-[#1E2638] text-slate-100 focus-visible:ring-emerald-500" />
          </div>
          {error && (
            <div data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          <Button type="submit" data-testid="login-submit-btn" disabled={loading}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
          </Button>
        </form>

        <div className="flex items-center justify-center gap-2 mt-6 text-xs text-slate-500">
          <ShieldCheck className="w-3.5 h-3.5" />
          Secured signaling over encrypted WebSocket
        </div>

        <div className="text-center mt-8" data-testid="login-footer">
          <div className="text-[11px] font-mono uppercase tracking-widest text-slate-600">
            © {new Date().getFullYear()} SCHARPZ Limited · All rights reserved
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-700 mt-1">
            Private infrastructure — unauthorized access prohibited &amp; monitored
          </div>
        </div>
      </div>
    </div>
  );
}
